import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Panel from '../Panel';
import {
  SubPanel,
  SubPanelRow,
  PanelRowDescriptor,
  PanelRowTextForm,
  PanelButtonRow,
  PanelContent,
  StyledForm,
  FullPanel
} from 'components/PanelComponents';
import { BlueBorderButton, BlueButton } from 'components/Button';
import {
  NO_PARENT_OPTION_VALUE,
  RESOURCE_TYPES,
  SIDEPANEL_STATES
} from 'utils/appConstants';
import {
  createAggregator,
  duplicateAggregator
} from '../../reducers/aggregator/actions';
import {
  setSidePanel,
  revertSidePanel,
  closeSidePanel
} from 'reducers/sidePanel/actions';
import { determineBack } from 'utils/determineBack';
import {
  POLL_AGGREGATORS,
  subscribe,
  unsubscribe
} from 'reducers/polling/actions';

const SUBSCRIBER_NAME = 'NewAggregator';
const pollRate = parseInt(window.env.REACT_APP_GLOBAL_POLL_RATE_FREQUENCY);

const NewAggregator = ({ isActive }) => {
  const dispatch = useDispatch();
  const sidePanel = useSelector(state => state.sidePanel);
  const [aggregatorId, setAggregatorId] = useState('');
  const [lfdi, setLfdi] = useState('');
  const [registrationPin, setRegistrationPin] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [registrationPinIsError, setRegistrationPinIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aggregatorIdIsError, setAggregatorIdIsError] = useState(false);
  const [lfdiIsError, setLfdiIsError] = useState(false);
  const aggregators = useSelector(state => state.aggregator.aggregators);

  useEffect(() => {
    if (isActive) {
      dispatch(
        subscribe({
          subscriber: SUBSCRIBER_NAME,
          pollTypes: [
            {
              type: POLL_AGGREGATORS
            }
          ],
          pollRate
        })
      );
    } else {
      dispatch(unsubscribe(SUBSCRIBER_NAME));
    }
    return () => {
      if (isActive) {
        dispatch(unsubscribe(SUBSCRIBER_NAME));
      }
    };
  }, [dispatch, isActive]);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  const handleInputChange = event => {
    const target = event.target;
    const value = target.value === NO_PARENT_OPTION_VALUE ? null : target.value;
    const name = target.name;
    if (name === 'aggregatorId') {
      setAggregatorId(value);
    } else if (name === 'lfdi') {
      setLfdi(value);
    } else if (name === 'registrationPin') {
      setRegistrationPin(value);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    setIsSubmitting(true);

    // TODO: Return and polish validation.

    let passed = true;

    if (
      !aggregatorId ||
      !/^[a-zA-Z0-9_.~-]*$/.test(aggregatorId) ||
      aggregatorId.length > 64
    ) {
      setAggregatorIdIsError(true);
      passed = false;
    } else {
      setAggregatorIdIsError(false);
    }

    if (!/^[a-zA-Z0-9]*$/gi.test(lfdi) || lfdi.length !== 40) {
      setLfdiIsError(true);
      passed = false;
    } else {
      setLfdiIsError(false);
    }

    if (!passed) {
      return;
    }

    const requestObject = {
      aggregatorId,
      lfdi
    };

    if (parseInt(registrationPin) >= 0) {
      requestObject.registrationPin = parseInt(registrationPin);
    }
    const alreadyExists = aggregators.find(
      element => element.aggregatorId === aggregatorId
    );

    if (alreadyExists) {
      return dispatch(duplicateAggregator(aggregatorId));
    }

    try {
      await dispatch(createAggregator(requestObject));
      dispatch(
        setSidePanel({
          entity: RESOURCE_TYPES.AGGREGATOR,
          state: SIDEPANEL_STATES.READ,
          id: aggregatorId
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isActive || (isActive && isLoading)) {
    return null;
  } else {
    return (
      <StyledForm onSubmit={handleSubmit}>
        <FullPanel>
          <Panel title="New Aggregator" primaryHeader />
          <PanelContent>
            <SubPanel>
              <SubPanelRow>
                <PanelRowDescriptor>ID</PanelRowDescriptor>
                <PanelRowTextForm
                  type="text"
                  name="aggregatorId"
                  onChange={handleInputChange}
                  isError={aggregatorIdIsError}
                  required
                  minLength="1"
                  maxLength="64"
                  pattern="[a-zA-Z0-9_.~-]*$"
                />
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>LFDI</PanelRowDescriptor>
                <PanelRowTextForm
                  type="text"
                  name="lfdi"
                  onChange={handleInputChange}
                  isError={lfdiIsError}
                  minLength="40"
                  maxLength="40"
                  pattern="[0-9a-zA-Z]+"
                />
              </SubPanelRow>

              <SubPanelRow>
                <PanelRowDescriptor>
                  Registration Pin (Optional)
                </PanelRowDescriptor>
                <PanelRowTextForm
                  type="number"
                  name="registrationPin"
                  onChange={handleInputChange}
                  isError={registrationPinIsError}
                  min="0"
                  max="999999"
                  step="1"
                />
              </SubPanelRow>
            </SubPanel>
          </PanelContent>
          <PanelButtonRow>
            <BlueBorderButton
              type="button"
              onClick={() =>
                determineBack(
                  sidePanel,
                  () => dispatch(revertSidePanel()),
                  () => dispatch(closeSidePanel())
                )
              }
            >
              Cancel
            </BlueBorderButton>
            <BlueButton type="submit">Create Aggregator</BlueButton>
          </PanelButtonRow>
        </FullPanel>
      </StyledForm>
    );
  }
};

export default NewAggregator;
